import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Lock, CheckCircle, PlayCircle, FileText, Award } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../services/api';

// Universidade Elosaúde — detalhe do curso + player (fatia 2).
// Player embute YouTube/Vimeo via SDK oficial, mede o tempo assistido e manda
// heartbeat a cada 5s; o SERVIDOR decide percent/conclusão (≥90%, anti-fraude).
// Prova + certificado entram na fatia 3.

const COR = { primaria: 'var(--brand, #20a490)', sucesso: '#27ae60', alerta: '#f39c12', info: '#3498db' };

// ---- carregadores de SDK (uma vez por página) ----
let ytPromise = null;
function carregarYT() {
  if (window.YT && window.YT.Player) return Promise.resolve(window.YT);
  if (ytPromise) return ytPromise;
  ytPromise = new Promise((resolve) => {
    const anterior = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => { if (anterior) anterior(); resolve(window.YT); };
    if (!document.getElementById('yt-iframe-api')) {
      const s = document.createElement('script');
      s.id = 'yt-iframe-api';
      s.src = 'https://www.youtube.com/iframe_api';
      document.body.appendChild(s);
    }
  });
  return ytPromise;
}
let vimeoPromise = null;
function carregarVimeo() {
  if (window.Vimeo && window.Vimeo.Player) return Promise.resolve(window.Vimeo);
  if (vimeoPromise) return vimeoPromise;
  vimeoPromise = new Promise((resolve) => {
    if (!document.getElementById('vimeo-player-api')) {
      const s = document.createElement('script');
      s.id = 'vimeo-player-api';
      s.src = 'https://player.vimeo.com/api/player.js';
      s.onload = () => resolve(window.Vimeo);
      document.body.appendChild(s);
    } else {
      const check = setInterval(() => {
        if (window.Vimeo && window.Vimeo.Player) { clearInterval(check); resolve(window.Vimeo); }
      }, 100);
    }
  });
  return vimeoPromise;
}

const AcademiaCurso = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [curso, setCurso] = useState(null);
  const [aulas, setAulas] = useState([]);
  const [progresso, setProgresso] = useState({}); // aula_id -> {percent, concluido, assistido_seg}
  const [aulaAtual, setAulaAtual] = useState(null);
  const [loading, setLoading] = useState(true);

  const hostRef = useRef(null);     // div host do player
  const playerRef = useRef(null);   // {tipo, inst}
  const hbRef = useRef(null);       // interval do heartbeat
  const tempoRef = useRef(0);       // segundos atuais (vimeo via timeupdate)
  const aulaRef = useRef(null);     // aula atual p/ uso nos callbacks

  useEffect(() => { aulaRef.current = aulaAtual; tempoRef.current = 0; }, [aulaAtual]);

  // ---- carga inicial ----
  useEffect(() => {
    (async () => {
      try {
        const [cRes, aRes, pRes] = await Promise.all([
          api.get(`/academia/cursos/${id}`),
          api.get(`/academia/cursos/${id}/aulas`),
          api.get('/academia/meu-progresso'),
        ]);
        setCurso(cRes.data);
        const lista = aRes.data || [];
        setAulas(lista);
        const mapa = {};
        (pRes.data || []).forEach((p) => { mapa[p.aula_id] = p; });
        setProgresso(mapa);
        if (lista.length) setAulaAtual(lista[0]);
      } catch (e) {
        const detail = e.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Erro ao carregar o curso');
      }
      setLoading(false);
    })();
  }, [id]);

  const segAtual = useCallback(() => {
    const p = playerRef.current;
    if (!p) return 0;
    if (p.tipo === 'vimeo') return Math.floor(tempoRef.current || 0);
    try { return p.inst.getCurrentTime ? Math.floor(p.inst.getCurrentTime()) : 0; } catch { return 0; }
  }, []);

  const enviarProgresso = useCallback(async () => {
    const aula = aulaRef.current;
    if (!aula) return;
    const seg = segAtual();
    if (seg <= 0) return;
    try {
      const { data } = await api.post(`/academia/aulas/${aula.id}/progresso`, { assistido_seg: seg });
      setProgresso((prev) => ({ ...prev, [aula.id]: data }));
    } catch { /* silencioso — heartbeat */ }
  }, [segAtual]);

  const pararHB = useCallback(() => {
    if (hbRef.current) { clearInterval(hbRef.current); hbRef.current = null; enviarProgresso(); }
  }, [enviarProgresso]);

  const iniciarHB = useCallback(() => {
    if (hbRef.current) return;
    hbRef.current = setInterval(enviarProgresso, 5000);
  }, [enviarProgresso]);

  const destruirPlayer = useCallback(() => {
    const p = playerRef.current;
    try { if (p?.inst?.destroy) p.inst.destroy(); } catch { /* noop */ }
    playerRef.current = null;
  }, []);

  // ---- monta o player quando muda a aula ----
  useEffect(() => {
    if (!aulaAtual || !hostRef.current) return;
    let cancelado = false;
    pararHB();
    destruirPlayer();
    hostRef.current.innerHTML = '<div id="academia-player-alvo"></div>';

    if (aulaAtual.tipo === 'vimeo') {
      carregarVimeo().then((V) => {
        if (cancelado) return;
        const vp = new V.Player('academia-player-alvo', { id: Number(aulaAtual.fonte), responsive: true });
        vp.on('play', iniciarHB);
        vp.on('pause', pararHB);
        vp.on('ended', () => { pararHB(); });
        vp.on('timeupdate', (d) => { tempoRef.current = d.seconds; });
        playerRef.current = { tipo: 'vimeo', inst: vp };
      });
    } else if (aulaAtual.tipo === 'youtube') {
      carregarYT().then((YT) => {
        if (cancelado) return;
        const yp = new YT.Player('academia-player-alvo', {
          videoId: aulaAtual.fonte,
          playerVars: { rel: 0, modestbranding: 1 },
          events: {
            onStateChange: (e) => {
              if (e.data === YT.PlayerState.PLAYING) iniciarHB();
              else pararHB();
            },
          },
        });
        playerRef.current = { tipo: 'youtube', inst: yp };
      });
    }
    return () => { cancelado = true; pararHB(); destruirPlayer(); };
  }, [aulaAtual]);

  // libera a aula se for a 1ª, ou se a anterior não trava, ou se a anterior foi concluída
  const aulaLiberada = (idx) => {
    if (idx === 0) return true;
    const ant = aulas[idx - 1];
    if (!ant.bloqueia_proximo) return true;
    return !!progresso[ant.id]?.concluido;
  };

  const totalConcluidas = aulas.filter((a) => progresso[a.id]?.concluido).length;
  const tudoConcluido = aulas.length > 0 && totalConcluidas === aulas.length;

  if (loading) return <div className="loading"><div className="spinner"></div></div>;
  if (!curso) return null;

  return (
    <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
      <button className="btn btn-secondary" style={{ marginBottom: '1rem' }} onClick={() => navigate('/academia')}>
        <ArrowLeft size={18} /> Voltar à Universidade
      </button>

      <h2 style={{ margin: '0 0 0.25rem' }}>{curso.titulo}</h2>
      {curso.descricao && <p style={{ color: 'var(--gray-500)', marginBottom: '1rem' }}>{curso.descricao}</p>}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 320px', gap: '1.25rem', alignItems: 'start' }}>
        {/* Player + aula atual */}
        <div>
          <div style={{ background: '#000', borderRadius: '10px', overflow: 'hidden', aspectRatio: '16 / 9' }}>
            {aulaAtual?.tipo === 'texto' ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', gap: '0.5rem' }}>
                <FileText size={20} /> Conteúdo em texto
              </div>
            ) : (
              <div ref={hostRef} style={{ width: '100%', height: '100%' }} />
            )}
          </div>

          {aulaAtual && (
            <div style={{ marginTop: '0.75rem' }}>
              <strong style={{ fontSize: '1rem' }}>{aulaAtual.titulo}</strong>
              {(() => {
                const p = progresso[aulaAtual.id];
                const pct = p?.percent || 0;
                return (
                  <div style={{ marginTop: '0.4rem' }}>
                    <div style={{ height: '6px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: `linear-gradient(90deg, ${COR.primaria}, ${COR.sucesso})`, transition: 'width 0.4s' }} />
                    </div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', marginTop: '0.3rem' }}>
                      {p?.concluido ? <span style={{ color: COR.sucesso, fontWeight: 600 }}>✓ Aula concluída</span> : `${pct}% assistido (conclui em 90%)`}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {tudoConcluido && (
            <div style={{ marginTop: '1rem', padding: '0.85rem 1rem', borderRadius: '10px', background: '#e7f7f3', border: `1px solid ${COR.primaria}`, display: 'flex', alignItems: 'center', gap: '0.6rem', color: '#16323a' }}>
              <Award size={20} style={{ color: COR.sucesso }} />
              <span>Você concluiu todas as aulas. A <strong>prova e o certificado</strong> entram na próxima etapa.</span>
            </div>
          )}
        </div>

        {/* Lista de aulas */}
        <div style={{ background: '#fff', border: '1px solid var(--gray-200)', borderRadius: '12px', overflow: 'hidden' }}>
          <div style={{ padding: '0.75rem 0.9rem', borderBottom: '1px solid var(--gray-200)', fontWeight: 600, fontSize: '0.9rem', color: '#16323a' }}>
            Aulas <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}>· {totalConcluidas}/{aulas.length}</span>
          </div>
          {aulas.map((a, idx) => {
            const liberada = aulaLiberada(idx);
            const concl = progresso[a.id]?.concluido;
            const ativa = aulaAtual?.id === a.id;
            return (
              <button
                key={a.id}
                type="button"
                disabled={!liberada}
                onClick={() => liberada && setAulaAtual(a)}
                style={{
                  width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '0.6rem',
                  padding: '0.7rem 0.9rem', border: 'none', borderBottom: '1px solid #f1f5f9',
                  background: ativa ? '#e7f7f3' : '#fff', cursor: liberada ? 'pointer' : 'not-allowed',
                  color: liberada ? '#16323a' : '#a8b4ba',
                }}
              >
                <span style={{ flexShrink: 0 }}>
                  {concl ? <CheckCircle size={17} style={{ color: COR.sucesso }} />
                    : !liberada ? <Lock size={16} />
                    : <PlayCircle size={17} style={{ color: ativa ? COR.primaria : '#94a3b8' }} />}
                </span>
                <span style={{ flex: 1, fontSize: '0.84rem', fontWeight: ativa ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {idx + 1}. {a.titulo}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AcademiaCurso;
